/**
 * Primary UI component for Form Sections
 */

import { Tooltip } from "@/base";
import { Button, cn, tv } from "@heroui/react";
import { IconBulb, IconInfoCircle } from "@tabler/icons-react";
import type { FC, ReactNode } from "react";

export interface FormSectionProps {
    children: ReactNode;
    title: ReactNode;
    titleEndContent?: ReactNode;
    description?: ReactNode;
    hint?: ReactNode;
    info?: ReactNode;
    direction?: "vertical" | "horizontal";
    cols?: 1 | 2 | 3 | 4 | 5 | 6;
    fullWidth?: boolean;
    isDisabled?: boolean;
    renderEmpty?: boolean;
    size?: "sm" | "md" | "lg";
}

const formSection = tv({
    slots: {
        base: "",
        main: "flex flex-col",
        content: "grid gap-6 flex-1 min-w-0",
        info: "flex-1 pb-4",
        description: "text-small text-foreground-500",
    },
    variants: {
        direction: {
            horizontal: {
                main: "xl:flex-row",
                content: "xl:items-end xl:justify-center",
                info: "xl:max-w-sm xl:pr-4 xl:pb-0",
            },
            vertical: {
                content: "sm:max-w-6xl",
            },
        },
        fullWidth: {
            true: {
                main: "xl:justify-between",
                content: "sm:max-w-full",
            },
            false: {
                content: "xl:max-w-3xl",
            },
        },
        cols: {
            1: {
                content: "grid-cols-1",
            },
            2: {
                content: "grid-cols-1 lg:grid-cols-2",
            },
            3: {
                content: "grid-cols-1 lg:grid-cols-2 xl:grid-cols-3",
            },
            4: {
                content:
                    "grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4",
            },
            5: {
                content:
                    "grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5",
            },
            6: {
                content:
                    "grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6",
            },
        },
        isDisabled: {
            true: {
                main: "opacity-50 pointer-events-none",
            },
        },
    },
    defaultVariants: {
        direction: "vertical",
        fullWidth: false,
        cols: 1,
        isDisabled: false,
    },
});

export const FormSection: FC<FormSectionProps> = ({
    children,
    title,
    titleEndContent,
    description,
    info,
    hint,
    renderEmpty,
    size = "lg",
    ...props
}) => {
    const v = formSection(props);

    if (renderEmpty) {
        return null;
    }

    return (
        <div className={v.base()}>
            <div className={v.main()}>
                <div className={v.info()}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1">
                            <h2
                                className={cn(
                                    "font-medium text-lg",
                                    size === "sm" && "text-sm",
                                    size === "md" && "text-md",
                                )}
                            >
                                {title}
                            </h2>
                            {info && (
                                <Tooltip color="primary" content={info}>
                                    <Button
                                        variant="light"
                                        isIconOnly={true}
                                        size="sm"
                                    >
                                        <IconInfoCircle
                                            stroke={1.5}
                                            size={18}
                                        />
                                    </Button>
                                </Tooltip>
                            )}
                            {hint && (
                                <Tooltip
                                    color="warning"
                                    placement="right"
                                    content={hint}
                                >
                                    <Button
                                        variant="light"
                                        isIconOnly={true}
                                        size="sm"
                                    >
                                        <IconBulb stroke={1.5} size={18} />
                                    </Button>
                                </Tooltip>
                            )}
                        </div>
                        {titleEndContent}
                    </div>
                    <div className={v.description()}>{description}</div>
                </div>
                <div className={v.content()}>{children}</div>
            </div>
        </div>
    );
};
